import React, { useState, useEffect, useCallback } from "react";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Box,
  Typography,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  styled
} from "@mui/material";
import { useDropzone } from "react-dropzone";
import { get, post, put } from "../../utility/fetchWeb";
import notification from "../../utility/notification";
import { PlayCircleIcon } from "lucide-react";

const VisuallyHiddenInput = styled('input')`
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  bottom: 0;
  left: 0;
  white-space: nowrap;
  width: 1px;
`;

export default function ArticleModal({ open, onClose, onSubmit, editingArticle, getArticles, isLoading }) {
  const [formValues, setFormValues] = useState({
    Title: "",
    FullDescription: "",
    CategoryId: "",
    IsPublished: false,
    IsFeatured: false,
    FeaturedImage: null,
    mediaType: null,
    mediaPreview: null
  });
  const [categories, setCategories] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize form when modal opens or editingArticle changes
  useEffect(() => {
    if (open) {
      if (editingArticle) {
        // Populate with existing article data for editing
        setFormValues({
          Title: editingArticle.title || "",
          FullDescription: editingArticle.fullDescription || editingArticle.subtitle || editingArticle.description || "",
          CategoryId: editingArticle.categoryId || "",
          IsPublished: editingArticle.isPublished || false,
          IsFeatured: editingArticle.isFeatured || false,
          FeaturedImage: null,
          mediaType: editingArticle.imageUrl || editingArticle.featuredImagePath ? 'image' : null,
          mediaPreview: editingArticle.imageUrl ? `${process.env.REACT_APP_MEDIA_URL}${editingArticle.imageUrl}` : 
                       editingArticle.featuredImagePath ? `${process.env.REACT_APP_MEDIA_URL}${editingArticle.featuredImagePath}` : null
        });
      } else {
        // Reset form for new article
        setFormValues({
          Title: "",
          FullDescription: "",
          CategoryId: "",
          IsPublished: false,
          IsFeatured: false,
          FeaturedImage: null,
          mediaType: null,
          mediaPreview: null
        });
      }
    }
  }, [open, editingArticle]);

  // Fetch categories
  useEffect(() => {
    async function loadCats() {
      try {
        const res = await get('/categories');
        if (res.code === 200) setCategories(res.data); 
      } catch {
        notification({ type: 'error', message: 'Failed to load categories' });
      }
    }
    loadCats();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues(prev => ({ ...prev, [name]: value }));
  };

  const handleRichText = (value) => {
    setFormValues(prev => ({ ...prev, FullDescription: value }));
  };

  const handleSwitch = (e) => {
    const { name, checked } = e.target;
    setFormValues(prev => ({ ...prev, [name]: checked }));
  };

  // Dropzone for image/video
  const onDrop = useCallback((files) => {
    if (files.length) {
      const file = files[0];
      const type = file.type.startsWith('image/') ? 'image' : 'video';
      const preview = URL.createObjectURL(file);
      setFormValues(prev => ({
        ...prev,
        FeaturedImage: file,
        mediaType: type,
        mediaPreview: preview
      }));
    }
  }, []);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: 'image/*,video/*'
  });

  const handleSubmit = async () => {
    setIsSubmitting(true);
    if (onSubmit) {
      const submitData = {
        Title: formValues?.Title,
        FullDescription: formValues?.FullDescription,
        ShortDescription: formValues?.FullDescription?.substring(0, 200) || "",
        CategoryId: formValues?.CategoryId,
        IsPublished: formValues?.IsPublished,
        IsFeatured: formValues?.IsFeatured,
        FeaturedImage: formValues?.FeaturedImage
      };
      
      await onSubmit(submitData);
      setIsSubmitting(false);
    }
  };

  // Preview component
  const MediaPreview = () => {
    if (!formValues?.mediaPreview) return null;
    return formValues?.mediaType === 'video' ? (
      <Box sx={{ position: 'relative', width: '100%', pt: '56.25%', mb: 2 }}>
        <Box
          component="video"
          src={formValues?.mediaPreview}
          poster={formValues?.mediaPreview}
          muted
          loop
          sx={{ position:'absolute', top:0, left:0, width:'100%', height:'100%', objectFit:'cover', borderRadius:1 }}
          onMouseEnter={e => e.currentTarget.play()}
          onMouseLeave={e => { e.currentTarget.pause(); e.currentTarget.currentTime =0; }}
        />
        <PlayCircleIcon sx={{ position:'absolute', top:'50%', left:'50%', transform:'translate(-50%,-50%)', color:'white', opacity:0.7 }} />
      </Box>
    ) : (
      <Box component="img" src={formValues?.mediaPreview} alt="preview" sx={{ width:'100%', height:200, objectFit:'cover', borderRadius:1, mb:2 }} />
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {editingArticle ? 'Edit Article' : 'Create New Article'}
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display:'flex', flexDirection:'column', gap:2 }}>
          <TextField 
            name="Title" 
            label="Title" 
            value={formValues?.Title} 
            onChange={handleChange} 
            fullWidth 
            required 
            placeholder="Enter article title..."
          />
          
          <Typography variant="subtitle1">Full Description</Typography>
          <ReactQuill 
            theme="snow" 
            value={formValues.FullDescription} 
            onChange={handleRichText}
            placeholder="Write your article content here..."
          />

          <MediaPreview />
          <Box
            {...getRootProps()}
            sx={{ 
              border:'2px dashed #ccc', 
              p:2, 
              textAlign:'center', 
              cursor:'pointer', 
              bgcolor:isDragActive?'#f0f0f0':'transparent',
              borderRadius: 1
            }}
          >
            <input {...getInputProps()} />
            <Typography>
              {formValues?.FeaturedImage 
                ? formValues?.FeaturedImage.name 
                : editingArticle?.imageUrl 
                  ? 'Current image selected. Click or drag to change.'
                  : 'Click or drag media here (image/video)'
              }
            </Typography>
          </Box>

          <FormControl fullWidth required>
            <InputLabel id="category-label">Category</InputLabel>
            <Select
              labelId="category-label"
              name="CategoryId"
              value={formValues?.CategoryId}
              onChange={handleChange}
              label="Category"
            >
              {categories?.map(cat => (
                <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControlLabel 
              control={
                <Switch 
                  name="IsPublished" 
                  checked={formValues?.IsPublished} 
                  onChange={handleSwitch} 
                />
              } 
              label="Publish Article" 
            />
            <FormControlLabel 
              control={
                <Switch 
                  name="IsFeatured" 
                  checked={formValues?.IsFeatured} 
                  onChange={handleSwitch} 
                />
              } 
              label="Feature Article" 
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained"
          disabled={isSubmitting ||isLoading || !formValues.Title || !formValues.FullDescription || !formValues.CategoryId}
        >
          {isLoading 
            ? (editingArticle ? 'Updating...' : 'Creating...') 
            : (editingArticle ? 'Update Article' : 'Create Article')
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
}
